<template>
  <v-dialog 
    v-model="show" 
    :max-width="maxWidth" 
    :persistent="persistent"
  >
    <v-card :class="{ 'confirm-dialog--centered': iconPosition === 'top' }">
      <!-- Иконка сверху по центру -->
      <div 
        v-if="showIcon && icon && iconPosition === 'top'" 
        class="confirm-dialog__icon-top"
      >
        <v-icon 
          :color="iconColor" 
          :size="iconSize"
        >
          {{ icon }}
        </v-icon>
      </div>

      <v-card-title 
        v-if="title" 
        :class="[
          'text-h5',
          'confirm-dialog__title',
          { 'confirm-dialog__title--centered': iconPosition === 'top' }
        ]"
      >
        <v-icon 
          v-if="showIcon && icon && iconPosition !== 'top'" 
          :color="iconColor" 
          left
        >
          {{ icon }}
        </v-icon>
        {{ title }}
      </v-card-title>
      
      <v-card-text 
        v-if="text || html"
        :class="{ 'confirm-dialog__text--centered': iconPosition === 'top' }"
      >
        <div 
          class="text-body-1" 
          v-if="html" 
          v-html="html"
        ></div>
        <div 
          class="text-body-1" 
          v-else
        >
          {{ text }}
        </div>
      </v-card-text>
      
      <v-card-actions :class="{ 'confirm-dialog__actions--centered': iconPosition === 'top' }">
        <v-spacer v-if="iconPosition !== 'top'"></v-spacer>
        <v-btn
          v-if="showCancelButton"
          data-testid="confirm-dialog-cancel"
          :color="cancelButtonColor"
          :text="!useFilledButtons"
          :outlined="useFilledButtons && !cancelButtonFilled"
          :class="{ 'confirm-dialog__btn': iconPosition === 'top' }"
          @click="cancel"
        >
          {{ cancelButtonText }}
        </v-btn>
        <v-btn
          data-testid="confirm-dialog-confirm"
          :color="confirmButtonColor"
          :text="!useFilledButtons"
          :class="{ 'confirm-dialog__btn': iconPosition === 'top' }"
          @click="confirm"
        >
          {{ confirmButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ConfirmDialog',
  
  data() {
    return {
      show: false,
      title: '',
      text: '',
      html: null,
      icon: null,
      iconColor: 'warning',
      iconSize: 64,
      iconPosition: 'left', // 'left' или 'top'
      showIcon: true,
      maxWidth: 500,
      persistent: true,
      showCancelButton: true,
      useFilledButtons: false,
      cancelButtonFilled: false,
      confirmButtonText: 'OK',
      confirmButtonColor: 'success darken-1',
      cancelButtonText: 'Cancel',
      cancelButtonColor: 'grey darken-1',
      resolvePromise: null,
      rejectPromise: null
    };
  },
  
  methods: {
    open(config = {}) {
      // Применяем конфигурацию
      this.title = config.title || '';
      this.text = config.text || '';
      this.html = config.html || null;
      this.icon = config.icon || 'mdi-alert-circle-outline';
      this.iconColor = config.iconColor || this.getIconColor(config.icon);
      this.iconSize = config.iconSize || 64;
      this.iconPosition = config.iconPosition || 'left';
      this.showIcon = config.showIcon !== false;
      this.maxWidth = config.maxWidth || 500;
      this.persistent = config.persistent !== false;
      this.showCancelButton = config.showCancelButton !== false;
      this.useFilledButtons = config.useFilledButtons || false;
      this.cancelButtonFilled = config.cancelButtonFilled || false;
      
      // Используем переводы по умолчанию, если не указаны
      this.confirmButtonText = config.confirmButtonText || (this.$t ? this.$t('custom.yes') : 'OK');
      this.confirmButtonColor = config.confirmButtonColor || 'success darken-1';
      this.cancelButtonText = config.cancelButtonText || (this.$t ? this.$t('custom.no') : 'Cancel');
      this.cancelButtonColor = config.cancelButtonColor || 'grey darken-1';
      
      this.show = true;
      
      // Возвращаем Promise
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    
    confirm() {
      this.show = false;
      if (this.resolvePromise) {
        this.resolvePromise(true);
      }
      this.reset();
    },
    
    cancel() {
      this.show = false;
      if (this.resolvePromise) {
        this.resolvePromise(false);
      }
      this.reset();
    },
    
    reset() {
      this.resolvePromise = null;
      this.rejectPromise = null;
    },
    
    getIconColor(icon) {
      // Автоматически определяем цвет иконки на основе типа
      if (icon === 'mdi-alert-circle-outline' || icon === 'mdi-alert') {
        return 'warning';
      } else if (icon === 'mdi-check-circle-outline' || icon === 'mdi-check') {
        return 'success';
      } else if (icon === 'mdi-close-circle-outline' || icon === 'mdi-close') {
        return 'error';
      } else if (icon === 'mdi-information-outline' || icon === 'mdi-information') {
        return 'info';
      }
      return 'warning';
    }
  }
};
</script>

<style scoped>
/* Стили для диалога с иконкой сверху */
.confirm-dialog--centered {
  text-align: center;
  padding-bottom: 8px;
}

.confirm-dialog__icon-top {
  display: flex;
  justify-content: center;
  padding-top: 32px;
  padding-bottom: 16px;
}

/* UAT 2026-08-07: Vuetify 2 глобально ставит .v-card__title { word-break:
   break-all } — длинный заголовок рвался посреди слова («платё|ж?»). Правило
   было только у centered-варианта; поднято на все confirm-диалоги. */
.confirm-dialog__title {
  word-break: break-word;
}

.confirm-dialog__title--centered {
  justify-content: center;
  text-align: center;
  word-break: break-word;
}

.confirm-dialog__text--centered {
  text-align: center;
  padding-bottom: 8px;
}

.confirm-dialog__actions--centered {
  justify-content: center;
  padding-bottom: 24px;
  gap: 12px;
}

.confirm-dialog__btn {
  min-width: 80px;
}
</style>

